/**
 * Logger Utility - Conditional logging based on environment and debug mode
 * 
 * Usage:
 * - logger.log(): Only in development or when debug mode is enabled
 * - logger.error(): Always logs (errors should always be visible)
 * - logger.warn(): Always logs (warnings should always be visible)
 * - logger.info(): Only in development or debug mode
 * - logger.sensitive(): ONLY in development (never in production, even with debug)
 * 
 * To enable debug mode in production: localStorage.setItem('debug', 'true')
 * To disable: localStorage.removeItem('debug')
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Check if debug mode is enabled via localStorage
 * Wrapped in try-catch to handle environments where localStorage might not be available
 */
const isDebugEnabled = () => {
  try {
    return localStorage.getItem('debug') === 'true';
  } catch {
    return false;
  }
};

/**
 * Should log regular debug messages?
 */
const shouldLog = () => isDevelopment || isDebugEnabled();

/**
 * Should log sensitive data? (ONLY in development, NEVER in production)
 */
const shouldLogSensitive = () => isDevelopment;

export const logger = {
  /**
   * Standard logging - only in development or debug mode
   */
  log: (...args) => {
    if (shouldLog()) {
      console.log(...args);
    }
  },

  /**
   * Error logging - ALWAYS log errors (they're important for debugging issues)
   */
  error: (...args) => {
    console.error(...args);
  },

  /**
   * Warning logging - ALWAYS log warnings
   */
  warn: (...args) => {
    console.warn(...args);
  },

  /**
   * Info logging - only in development or debug mode
   */
  info: (...args) => {
    if (shouldLog()) {
      console.info(...args);
    }
  },

  /**
   * Sensitive data logging - ONLY in development, NEVER in production
   * Use this for URLs, tokens, user data, API responses, etc.
   */
  sensitive: (...args) => {
    if (shouldLogSensitive()) {
      console.log('🔒 [SENSITIVE]', ...args);
    }
  },

  /**
   * Group logging - for organizing related logs
   */
  group: (label, callback) => {
    if (shouldLog()) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  },

  /**
   * Table logging - useful for arrays and objects
   */
  table: (data) => {
    if (shouldLog()) {
      console.table(data);
    }
  }
};

// Export a helper to check current logging state (useful for debugging)
export const getLoggingState = () => ({
  isDevelopment,
  isDebugEnabled: isDebugEnabled(),
  willLog: shouldLog(),
  willLogSensitive: shouldLogSensitive()
});

export default logger;

